import React, { useEffect, useState } from "react";
import { Trees, ExternalLink, ArrowRightLeft, AlertTriangle, Info } from "lucide-react";
import { api } from "@/api";
import { CopyButton } from "@/components/CopyButton";

const EXP = "https://explorer.cronos.org";
const fmt = (v, d = 2) => (v == null ? "—" : Number(v).toLocaleString(undefined, { maximumFractionDigits: d }));

function Stat({ label, value, sub, tid }) {
  return (
    <div className="card-tile" data-testid={tid}>
      <div className="text-xs text-slate-500">{label}</div>
      <div className="font-mono text-2xl font-black mt-1">{value}</div>
      {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
    </div>
  );
}

function Chart({ d }) {
  const [failed, setFailed] = useState(false);
  const embed = `https://dexscreener.com/cronos/${d.pool.toLowerCase()}?embed=1&theme=dark&trades=0&info=0`;
  return (
    <div className="card-tile mt-6 p-0 overflow-hidden" data-testid="nwood-chart">
      {d.listed && !failed
        ? <iframe title="NWOOD chart" src={embed} className="w-full h-[460px] border-0" onError={() => setFailed(true)} data-testid="nwood-chart-iframe" />
        : <div className="h-48 flex flex-col items-center justify-center text-center px-6 text-sm text-slate-400" data-testid="nwood-chart-pending">
            <p>{d.listed ? "The chart couldn't load here." : "DexScreener is indexing the first trade. The live chart appears here once it's listed."}</p>
          </div>}
      <div className="px-5 py-3 border-t border-white/5 flex justify-end">
        <a href={d.dexscreener_url} target="_blank" rel="noreferrer" className="text-xs text-emerald-300 underline flex items-center gap-1" data-testid="nwood-dexscreener-link">View on DexScreener <ExternalLink size={11} /></a>
      </div>
    </div>
  );
}

export default function NwoodPage() {
  const [d, setD] = useState(null);
  const [msg, setMsg] = useState("");
  useEffect(() => { const load = () => api.get("/nwood").then((r) => setD(r.data)).catch(() => {}); load(); const id = setInterval(load, 60000); return () => clearInterval(id); }, []);
  if (!d) return <div className="pt-32 text-center text-slate-400">Loading…</div>;
  const dx = d.dexscreener;
  const tx = dx?.txns_24h || {};
  const add = () => (window.ethereum
    ? window.ethereum.request({ method: "wallet_watchAsset", params: { type: "ERC20", options: { address: d.address, symbol: "NWOOD", decimals: 18 } } })
      .then(() => setMsg("NWOOD added to your wallet")).catch((e) => setMsg(e.message))
    : setMsg("No wallet found"));

  return (
    <div className="pt-24 pb-20" data-testid="nwood-page">
      <div className="max-w-6xl mx-auto px-4">
        <img src="/brand/nwood-header-1500x500.png" alt="Northwoods NWOOD misty forest banner" className="w-full aspect-[3/1] object-cover rounded-2xl border border-emerald-500/20 mb-6" data-testid="nwood-header-image" />
        <div className="chip inline-flex text-emerald-300 border-emerald-500/40"><Trees size={12} /> Cronos · VVS V3</div>
        <div className="flex flex-wrap items-end gap-4 mt-3">
          <img src="/brand/nwood-logo-round.png" alt="NWOOD logo" className="w-16 h-16 sm:w-20 sm:h-20 rounded-full" data-testid="nwood-logo" />
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black flex-1">Northwoods <span className="text-emerald-400 text-2xl">NWOOD</span></h1>
          <a href={d.vvs_url} target="_blank" rel="noreferrer" className="btn-tactile bg-emerald-500 hover:bg-emerald-400 text-[#0F101A] font-bold flex items-center gap-2 transition-colors" data-testid="nwood-trade-button"><ArrowRightLeft size={16} /> Trade on VVS</a>
        </div>
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <code className="font-mono text-xs sm:text-sm break-all" data-testid="nwood-address">{d.address}</code>
          <CopyButton value={d.address} tid="nwood-copy-address" label="NWOOD address" />
          <a href={`${EXP}/address/${d.address}#code`} target="_blank" rel="noreferrer" className="text-xs text-cyan-400 underline flex items-center gap-1" data-testid="nwood-explorer-link">Explorer <ExternalLink size={11} /></a>
          <button onClick={add} className="btn-tactile btn-ghost text-xs" data-testid="nwood-add-wallet">Add to wallet</button>
          {msg && <span className="text-xs text-slate-400">{msg}</span>}
        </div>

        <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Stat label="Price" value={`${fmt(dx?.price_cro || d.price_cro, 6)} CRO`} sub={d.price_usd ? `$${fmt(d.price_usd, 6)}` : null} tid="nwood-stat-price" />
          <Stat label="Pool liquidity" value={`${fmt(d.pool_cro, 0)} CRO`} sub={`${fmt(d.pool_nwood, 0)} NWOOD`} tid="nwood-stat-liquidity" />
          <Stat label="24h volume" value={dx ? `$${fmt(dx.volume_24h)}` : "—"} sub={dx ? `${dx.change_24h ?? 0}% 24h change` : "after DexScreener lists it"} tid="nwood-stat-volume" />
          <Stat label="24h trades" value={dx ? fmt((tx.buys || 0) + (tx.sells || 0), 0) : "—"} sub={dx ? `${tx.buys || 0} buys · ${tx.sells || 0} sells` : "after DexScreener lists it"} tid="nwood-stat-trades" />
        </div>

        <Chart d={d} />

        <div className="mt-6 grid md:grid-cols-2 gap-4">
          <div className="card-tile" data-testid="nwood-about">
            <h2 className="font-display text-xl font-bold flex items-center gap-2"><Info size={18} className="text-emerald-400" /> About NWOOD</h2>
            <ul className="mt-3 text-sm text-slate-300 space-y-2 list-disc pl-5">
              <li>20,000,000 fixed supply. No owner, and no new coins can ever be created.</li>
              <li>ROAST buyers earn 100 NWOOD per ROAST, claimable from Jan 1, 2028.</li>
              <li>The pool position is owned by the project Safe and is <b>not locked</b>.</li>
            </ul>
          </div>
          <div className="card-tile border-amber-500/40" data-testid="nwood-risk">
            <h2 className="font-display text-xl font-bold flex items-center gap-2"><AlertTriangle size={18} className="text-amber-400" /> Risk</h2>
            <p className="mt-3 text-sm text-slate-300">NWOOD pays no income and its price can go to zero. The pool is small, so each trade can move the price a lot. Only trade what you can afford to lose.</p>
          </div>
        </div>
      </div>
    </div>
  );
}
